import { inject, Injectable } from '@angular/core';

import { App } from '@capacitor/app';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { AppUpdate } from '@capawesome/capacitor-app-update';

import { ModalType } from '@core/models/modal.types';
import { LanguageService } from '@core/services/language.service';
import { ModalService } from '@core/services/modal.service';
import { PlatformService } from '@core/services/platform.service';

import type { CmsAppConfig } from '@shared/modals/app-update/app-update-modal.types';
import { AppUpdateModal } from '@shared/modals/app-update/app-update.modal';

import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class UpdateService {
  private readonly modalService = inject(ModalService);
  private readonly platformService = inject(PlatformService);
  private readonly languageService = inject(LanguageService);

  private hasChecked = false;

  async checkForUpdates(): Promise<void> {
    if (this.hasChecked) return;
    this.hasChecked = true;
    try {
      const promises: Promise<CmsAppConfig | null>[] = [this.getMaintenanceInfo()];

      if (!this.platformService.isWeb()) {
        promises.push(this.getAvailableAppInfo());
      }

      const [maintenance, available] = await Promise.all(promises);

      if (maintenance) await this.presentModal(maintenance);
      if (available) await this.presentModal(available);
    } catch {
      return;
    }
  }

  async openAppStore(): Promise<void> {
    try {
      await AppUpdate.openAppStore({ appId: environment.appleStoreAppId });
    } catch {
      return;
    }
  }

  private async getCurrentAppVersion(): Promise<string> {
    try {
      const info = await AppUpdate.getAppUpdateInfo();
      return Capacitor.getPlatform() === 'android'
        ? info.currentVersionName || info.currentVersionCode
        : info.currentVersionName;
    } catch {
      const info = await App.getInfo();
      return Capacitor.getPlatform() === 'android' ? info.version || info.build : info.version;
    }
  }

  private async getAvailableAppInfo(): Promise<CmsAppConfig | null> {
    // TODO app version info should come from app-config
    const config = await this.fetchCmsContent(environment.cmsVersionId);
    if (!config) return null;

    const currentVersion = await this.getCurrentAppVersion();
    const availableVersion = Capacitor.getPlatform() === 'android' ? config.android : config.iOS;
    if (!availableVersion) return null;

    if (!this.isNewerVersion(currentVersion, availableVersion)) return null;

    return config;
  }

  private isNewerVersion(current: string, available: string): boolean {
    const parse = (version: string): number[] =>
      version.split('.').map((part) => {
        const num = Number(part);
        return Number.isNaN(num) ? 0 : num;
      });

    const [currentMajor = 0, currentMinor = 0, currentPatch = 0] = parse(current);
    const [availableMajor = 0, availableMinor = 0, availablePatch = 0] = parse(available);

    if (availableMajor !== currentMajor) return availableMajor > currentMajor;
    if (availableMinor !== currentMinor) return availableMinor > currentMinor;
    return availablePatch > currentPatch;
  }

  private async getMaintenanceInfo(): Promise<CmsAppConfig | null> {
    // TODO app version info should come from app-config
    return this.fetchCmsContent(environment.cmsMaintenanceId);
  }

  private async presentModal(config: CmsAppConfig): Promise<void> {
    await this.modalService.openModal(AppUpdateModal, { data: config }, ModalType.CARD, {
      canDismiss: config.canDismiss
        ? true
        : async (_data?: unknown, role?: string) => role !== 'gesture' && role !== 'backdrop',
      backdropDismiss: !!config.canDismiss,
    });
  }

  private async fetchCmsContent(contentId: number): Promise<CmsAppConfig | null> {
    try {
      const locale = this.languageService.currentLanguage() || 'en';
      const response = await CapacitorHttp.get({
        url: `${environment.cmsUrl}/api/patient-app-config/${contentId}?locale=${locale}`,
      });

      const { data, status } = response;
      if (status >= 400 || !data) return null;

      return data?.value || null;
    } catch {
      return null;
    }
  }
}
